<template>
  <div
    ref="curtain"
    class="md:rounded-lg absolute md:relative md:w-[24.75rem] z-100 flex-col gap-4 p-4 duration-200 bg-white"
    :class="{
      'flex': activeClass,
      'hidden': !activeClass,
      'left-0 right-0 rounded-t-lg ': !floating,
      'left-4 right-4 md:left-0 md:right-0 rounded-lg ': floating,
      'h-full': fullscreen && alwaysTall
    }"
    :style="
      viewport.width >= $screen.md
        ? {
            maxHeight: `calc(100vh - 150px)`,
            marginTop: `-64px`
          }
        : {
            minHeight: '10rem',
            bottom: floating ? (parseInt($helpers.safeZone('bottom')) === 0 ? `34px` : $helpers.safeZone('bottom')) : 0
          }
    "
  >
    <div
      v-if="dragAllowed"
      class="flex items-center justify-center py-4 -my-4"
      @touchstart="dragAllowed ? onDragStart($event) : ''"
      @touchend="dragAllowed ? onDragEnd($event) : ''"
      @mousedown="dragAllowed ? onDragStart($event) : ''"
      @mouseup="dragAllowed ? onDragEnd($event) : ''"
      @touchmove="dragAllowed ? onDrag($event) : ''"
      @mousemove="dragAllowed ? onDrag($event) : ''"
    >
      <div class="w-12 h-1 rounded-lg pointer-events-none bg-silver"></div>
    </div>
    <div
      v-if="(title && historyMode) || (title && hasClose) || (historyMode && hasClose) || title"
      class="flex items-center justify-between"
      ref="title"
    >
      <ph-caret-left
        v-if="canAdvanceBackwards && historyMode"
        @click.native="
          $emit('back')
          back()
        "
        :size="24"
        class="text-black cursor-pointer"
      />
      <div v-else class="icon-placeholder"></div>
      <t-heading :tag="'h2'" :branded="true" class="font-semibold text-center">{{ title }}</t-heading>
      <ph-x v-if="hasClose && this.viewport.width < this.$screen.md" :size="24" class="text-black cursor-pointer" @click="close()" />
      <div v-else class="icon-placeholder"></div>
    </div>
    <div class="w-full" v-show="$slots['sub-title']" ref="subTitle">
      <slot name="sub-title"></slot>
    </div>
    <div
      class="overflow-x-hidden scrollbar-hidden"
      :class="fullscreen ? 'overflow-y-scroll h-full' : 'overflow-y-hidden h-full'"
      ref="content"
    >
      <slot></slot>
    </div>
    <div
      ref="footer"
      v-show="$slots.footer"
      class="w-full mt-auto"
      :style="!floating ? {paddingBottom: 'calc(0px + env(safe-area-inset-bottom))'} : {paddingBottom: 0}"
    >
      <slot name="footer"></slot>
    </div>
    <div class="absolute inset-x-0 flex items-center justify-center -bottom-16">
      <div
        v-if="!dragAllowed && viewport.width >= $screen.md"
        class="flex items-center justify-center gap-2 px-10 py-2 text-black bg-white rounded-lg cursor-pointer"
        @click="close()"
      >
        <ph-x :size="24" class="text-black" />
        <t-text>{{ $t('components.curtain.close') }}</t-text>
      </div>
    </div>
  </div>
</template>

<script>
  import handleViewport from '@/utils/mixins/handleViewport'
  export default {
    name: 't-curtain-tab',
    props: {
      historyMode: {
        type: Boolean,
        default: true
      },
      dragMode: {
        type: Boolean,
        default: true
      },
      hasClose: {
        type: Boolean,
        default: true
      },
      title: {
        type: String,
        default: ''
      },
      fullscreen: {
        type: [Boolean, Number],
        default: true
      },
      alwaysTall: {
        type: Boolean,
        default: false
      },
      group: {
        type: String,
        required: false,
        default: 'default'
      },
      name: {
        type: String,
        required: true
      },
      floating: {
        type: Boolean,
        default: false
      },
      preventBgScaling: {
        type: Boolean,
        default: false
      }
    },
    mixins: [handleViewport],
    computed: {
      dragAllowed() {
        if (this.viewport.width >= this.$screen.md) return false
        return this.dragMode
      },
      isDesktop() {
        return this.viewport.width >= this.$screen.md
      }
    },
    data() {
      return {
        cursorPositionY: 0,
        startingPoint: 0,
        isDragging: false,
        active: false,
        activeClass: false,
        first: false,
        canAdvanceBackwards: false,
        back: null,
        close: null,
        curtainWrapper: null,
        curtainOverlay: null
      }
    },
    watch: {
      active(val) {
        if (val) {
          setTimeout(
            () => {
              this.activeClass = true
              this.show()
            },
            this.first ? 0 : 200
          )
        } else {
          this.hideCurrentTab()
          setTimeout(() => {
            this.activeClass = false
          }, 200)
        }
      }
    },
    methods: {
      async show() {
        if (this.viewport.width < this.$screen.md) {
          this.$refs.curtain.style.top = '100vh'
          this.$refs.curtain.style.opacity = '1'
        } else {
          this.$refs.curtain.style.top = 'unset'
          this.$refs.curtain.style.opacity = '0'
        }
        document.getElementById('main').classList.add('duration-200')
        await this.$helpers.sleep(10)
        this.curtainWrapper.classList.replace('hidden', 'flex')
        await this.$helpers.sleep(10)
        if (this.fullscreen) {
          if (this.viewport.width < this.$screen.md) {
            document.getElementById('main').style.transform =
              !this.floating && !this.preventBgScaling ? (this.$helpers.isIOS() ? 'scale(.9)' : 'scale(.96)') : 'scale(1)'
            if (!this.floating && !this.preventBgScaling) document.getElementById('main').classList.add('rounded-lg')
            else document.getElementById('main').classList.remove('rounded-lg')
          } else {
            document.getElementById('main').style.transform = 'scale(1)'
            document.getElementById('main').classList.remove('rounded-lg')
          }
          if (this.viewport.width < this.$screen.md) {
            this.$refs.curtain.style.top = this.$helpers.isIOS()
              ? 'calc(10px + env(safe-area-inset-top))'
              : 'calc(2vh + 10px + env(safe-area-inset-top))'
            this.$refs.curtain.style.height = ''
          } else {
            this.$refs.curtain.style.opacity = '1'
          }
        } else {
          if (this.viewport.width < this.$screen.md) {
            document.getElementById('main').style.transform = 'scale(1)'
            document.getElementById('main').classList.remove('rounded-lg')
            this.$nextTick(() => {
              this.$refs.curtain.style.top = `calc(var(--1svh) * 100 - ${
                this.$refs.content.lastElementChild.offsetTop +
                this.$refs.content.lastElementChild.clientHeight +
                this.$refs.footer.clientHeight +
                18 +
                this.$refs.subTitle.clientHeight
              }px - 24px - ${
                this.floating
                  ? parseInt(this.$helpers.safeZone('bottom')) !== 0
                    ? parseFloat(this.$helpers.safeZone('bottom'))
                    : '34'
                  : 0
              }px)`
            })
          } else {
            let height = this.$refs.curtain.clientHeight
            this.$refs.curtain.style.height = height + 'px'
            this.$refs.curtain.style.opacity = '1'
          }
        }
        this.curtainOverlay.classList.replace('opacity-0', 'opacity-100')
        setTimeout(() => {
          document.getElementById('main').classList.remove('duration-200')
        }, 200)

        this.$nextTick(() => {
          if (this.$slots.footer) {
            if (this.floating) {
              this.$refs.footer.style.paddingBottom = '0px'
            }
          }
        })
      },
      async hide() {
        document.getElementById('main').classList.add('duration-200')
        await this.$helpers.sleep(10)
        if (this.viewport.width < this.$screen.md) {
          this.$refs.curtain.style.top = '100vh'
        } else {
          this.$refs.curtain.style.opacity = '0'
        }

        if (this.viewport.width < this.$screen.md) {
          document.getElementById('main').style.transform = 'scale(1)'
        }
        this.curtainOverlay.classList.replace('opacity-100', 'opacity-0')
        document.getElementById('main').classList.remove('rounded-lg')
        await this.$helpers.sleep(200)
        this.curtainWrapper.classList.replace('flex', 'hidden')
        document.getElementById('main').classList.remove('duration-200')
        await this.$helpers.sleep(10)
        this.active = false
      },
      hideCurrentTab() {
        if (this.viewport.width < this.$screen.md) {
          this.$refs.curtain.style.top = '100vh'
        } else {
          this.$refs.curtain.style.opacity = '0'
          this.$refs.curtain.style.height = ''
        }
      },
      onDragStart(e) {
        this.startingPoint = (e.touches && e.touches[0].pageY) || (e.changedTouches && e.changedTouches[0].pageY) || e.pageY
        this.isDragging = true
        this.$refs.curtain.classList.replace('duration-200', 'duration-0')
      },
      onDrag(e) {
        this.cursorPositionY = (e.touches && e.touches[0].pageY) || (e.changedTouches && e.changedTouches[0].pageY) || e.pageY
        if (this.isDragging && this.cursorPositionY >= this.startingPoint) {
          this.$refs.curtain.style.top = `${this.cursorPositionY}px`
        }
      },
      onDragEnd() {
        this.isDragging = false
        if (this.startingPoint - this.cursorPositionY < -80) {
          this.$refs.curtain.classList.replace('duration-0', 'duration-200')
          this.close()
        } else {
          this.$refs.curtain.classList.replace('duration-0', 'duration-200')
          setTimeout(() => {
            this.$refs.curtain.style.top = this.fullscreen
              ? this.$helpers.isIOS()
                ? 'calc(10px + env(safe-area-inset-top))'
                : 'calc(2vh + 10px + env(safe-area-inset-top))'
              : `calc(100vh - ${this.$refs.curtain.clientHeight}px - ${
                  this.floating
                    ? parseInt(this.$helpers.safeZone('bottom')) !== 0
                      ? parseFloat(this.$helpers.safeZone('bottom'))
                      : '34'
                    : 0
                }px)`
          }, 0)
        }
      },
      onMobileViewEnter() {
        if (this.active) this.show()
      },
      onDesktopViewEnter() {
        if (this.active) this.show()
      }
    }
  }
</script>
